import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/ui/dialog';
import * as Sentry from '@sentry/react';
import { PaymentContent, Warning } from './style';
import { Button } from 'components/ui/button';
import { Typography } from 'components/ui/typography';
import { CreditCard, Landmark, Loader2 } from 'lucide-react';
import {
  AddressElement,
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  loadStripe,
  StripeElementsOptions,
  StripePaymentElementChangeEvent,
} from '@stripe/stripe-js';
import { useState } from 'react';
import { useAuth } from 'providers/AuthProvider';
import { useMutation } from '@apollo/client/react';
import { ATTACH_PAYMENT_METHOD } from 'app/graphql';
import { useAppSelector } from 'app/store';
import { selectTheme } from 'app/store/theme';
import { selectUserInfo } from 'app/store/user';
import { env } from 'config/env';
import { usBankAccountPaymentMethodOptions } from 'pages/Billing/paymentMethodOptions';
import { PaymentIcon, PaymentTypeExtended } from 'react-svg-credit-card-payment-icons';
import { PaymentMethod } from 'app/graphql/generated/admin/graphql';
import { toast } from 'sonner';
import { canWrite } from 'app/access';

type Props = {
  onSubmit: (paymentMethod: PaymentMethod) => void;
};

const stripePromise = loadStripe(env.VITE_STRIPE_KEY);

function paymentMethodElementsOptions(theme: string): StripeElementsOptions {
  return {
    mode: 'payment',
    amount: 100,
    currency: 'usd',
    paymentMethodCreation: 'manual',
    paymentMethodTypes: ['us_bank_account', 'card'],
    paymentMethodOptions: usBankAccountPaymentMethodOptions,
    appearance: {
      theme: theme === 'dark' ? 'night' : 'flat',
      labels: 'floating',
      variables: { colorPrimary: 'rgb(78, 213, 88)', borderRadius: '0px' },
    },
  };
}

export const PaymentDialog = (props: Props) => {
  const theme = useAppSelector(selectTheme);
  return (
    <Elements stripe={stripePromise} options={paymentMethodElementsOptions(theme)}>
      <PaymentDialogForm {...props} />
    </Elements>
  );
};

const PaymentDialogForm = ({ onSubmit }: Props) => {
  const { user } = useAuth();
  const userState = useAppSelector(selectUserInfo);
  const canEditBilling = canWrite(userState, 'billing');
  const stripe = useStripe();
  const elements = useElements();
  const [showWarning, setShowWarning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [hostedVerificationUrl, setHostedVerificationUrl] = useState<string | null>(null);

  const [attachPaymentMethod] = useMutation(ATTACH_PAYMENT_METHOD, {
    context: { clientName: 'admin' },
  });

  const handleChange = async (event: StripePaymentElementChangeEvent) => {
    if (event.value.type === 'card') {
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  };

  const handleSubmit = async () => {
    if (!canEditBilling) {
      return;
    }

    setIsSubmitting(true);
    let awaitingVerification = false;
    try {
      if (!stripe || !elements || !user) {
        setIsSubmitting(false);
        return;
      }
      await elements.submit();

      const pm = await stripe?.createPaymentMethod({
        elements,
        params: { metadata: { developerId: user.uid } },
      });

      if (pm.error) {
        setIsSubmitting(false);
        return;
      }

      if (!pm.paymentMethod) {
        throw new Error(
          'Failed to create payment method - payment method does not exist in stripe',
        );
      }

      const name = pm.paymentMethod.billing_details.name;
      const firstName = name ? name.split(' ')[0] : user.displayName?.split(' ')[0] ?? '';
      const lastName = name ? name.split(' ')[1] ?? '' : user.displayName?.split(' ')[1] ?? '';

      const res = await attachPaymentMethod({
        variables: {
          input: {
            id: pm.paymentMethod.id,
            billingDetails: {
              firstName,
              lastName,
            },
          },
        },
      });
      if (res.errors) {
        throw new Error(JSON.stringify(res.errors));
      }

      const attached = res.data?.attachPaymentMethod;
      const pendingVerification = attached?.pendingMicrodepositVerification;
      if (pendingVerification) {
        setHostedVerificationUrl(pendingVerification.hostedVerificationUrl);
        awaitingVerification = true;
        return;
      }

      const stripeConfig = attached?.stripeConfig;
      if (!stripeConfig) {
        throw new Error('customer does not exist');
      }
      const paymentMethod = stripeConfig.paymentMethod;

      if (!paymentMethod) {
        throw new Error('payment method does not exist');
      }
      onSubmit(paymentMethod);
      toast.success('Payment method updated', {
        id: 'payment-added-success',
      });
    } catch (error) {
      Sentry.captureException('Failed to attach payment method - ' + error);
      toast.error('Error adding payment method', {
        id: 'payment-added-failed',
      });
    } finally {
      setIsSubmitting(false);
      if (!awaitingVerification) {
        setOpen(false);
      }
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setHostedVerificationUrl(null);
    }
  };

  const getPaymentIcon = () => {
    const paymentMethod = userState?.stripe?.paymentMethod;
    switch (paymentMethod?.__typename) {
      case 'PaymentMethodCard':
        return (
          <PaymentIcon
            type={paymentMethod.brand as PaymentTypeExtended}
            format="flatRounded"
            width={30}
            height={19}
            style={{ borderRadius: '3px' }}
          />
        );
      case 'PaymentMethodACH':
      case 'PaymentMethodUnknown':
        return <Landmark className="h-4 w-4" />;
    }
    return (
      <PaymentIcon
        type="Generic"
        format="flatRounded"
        width={30}
        height={19}
        style={{ borderRadius: '3px' }}
      />
    );
  };

  const getLast4 = () => {
    const paymentMethod = userState?.stripe?.paymentMethod;
    switch (paymentMethod?.__typename) {
      case 'PaymentMethodCard':
        return paymentMethod?.last4;
      case 'PaymentMethodACH':
        return paymentMethod?.last4AccountNumber;
      case 'PaymentMethodUnknown':
        return paymentMethod?.brand ?? 'Unknown Payment Method';
    }
    return '';
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {canEditBilling && (
        <DialogTrigger asChild disabled={!canEditBilling}>
          <Button type="button" variant="outline" size="sm" className="gap-2">
            <CreditCard className="h-3.5 w-3.5" />
            {!userState?.stripe?.paymentMethod ? (
              <span>Add Payment Method</span>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                Update payment: {getPaymentIcon()} •••{getLast4()}
              </span>
            )}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent>
        {hostedVerificationUrl ? (
          <>
            <DialogHeader>
              <DialogTitle>Verify your bank account</DialogTitle>
              <DialogDescription>
                We saved this bank account. Microdeposits take 1-2 business days. This bank account
                becomes your payment method once verified.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button asChild>
                <a href={hostedVerificationUrl} target="_blank" rel="noopener noreferrer">
                  Verify bank account
                </a>
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Add payment method</DialogTitle>
              <DialogDescription>We'll use this to charge your monthly balance.</DialogDescription>
            </DialogHeader>
            <PaymentContent>
              <Typography variant="label" as="div">
                Billing address
              </Typography>
              <AddressElement options={{ mode: 'billing' }} />
              <Typography variant="label" as="div">
                Payment method
              </Typography>
              <PaymentElement
                options={{
                  paymentMethodOrder: ['us_bank_account', 'card'],
                  fields: { billingDetails: { name: 'never', address: 'never' } },
                }}
                onChange={handleChange}
              />
              {showWarning && (
                <Warning>
                  Note: Using a card will incur an additional 3% charge to your monthly balance
                </Warning>
              )}
            </PaymentContent>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
